/**
 * Validation utilities for API responses
 */

import { Project, Column } from '../types';

/**
 * Custom validation error class
 */
export class ValidationError extends Error {
  field: string;
  value: unknown;
  
  constructor(message: string, field: string = '', value: unknown = null) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
    this.value = value;
  }
}

/**
 * Validate that a value is a string
 */
export function validateString(value: unknown, fieldName: string): string {
  if (typeof value !== 'string') {
    throw new ValidationError(`Expected string for ${fieldName}`, fieldName, value);
  }
  return value;
}

/**
 * Validate that a value is a number
 */
export function validateNumber(value: unknown, fieldName: string): number {
  if (typeof value !== 'number') {
    throw new ValidationError(`Expected number for ${fieldName}`, fieldName, value);
  }
  return value;
}

/**
 * Validate that a value is a boolean
 */
export function validateBoolean(value: unknown, fieldName: string): boolean {
  if (typeof value !== 'boolean') {
    throw new ValidationError(`Expected boolean for ${fieldName}`, fieldName, value);
  }
  return value;
}

/**
 * Validate that a value is an array
 */
export function validateArray<T>(value: unknown, fieldName: string): T[] {
  if (!Array.isArray(value)) {
    throw new ValidationError(`Expected array for ${fieldName}`, fieldName, value);
  }
  return value as T[];
}

/**
 * Validate that a value is an object
 */
export function validateObject(value: unknown, fieldName: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new ValidationError(`Expected object for ${fieldName}`, fieldName, value);
  }
  return value as Record<string, unknown>;
}

/**
 * Validate a project response from the API
 */
export function validateProject(data: unknown): Project {
  const obj = validateObject(data, 'project');
  validateString(obj.projectId, 'projectId');
  validateString(obj.name, 'name');
  return obj as unknown as Project;
}

/**
 * Validate an array of projects
 */
export function validateProjectArray(data: unknown): Project[] {
  const arr = validateArray<unknown>(data, 'projects');
  return arr.map(item => validateProject(item));
}

/**
 * Validate list projects response
 */
export function validateListProjectsResponse(data: unknown): { projects: Project[]; totalCount: number } {
  const obj = validateObject(data, 'listProjectsResponse');
  const projects = validateArray<unknown>(obj.projects, 'projects');
  const totalCount = typeof obj.totalCount === 'number' ? obj.totalCount : projects.length;
  return {
    projects: projects.map(item => validateProject(item)),
    totalCount
  };
}

/**
 * Validate a column response from the API
 */
export function validateColumn(data: unknown): Column {
  const obj = validateObject(data, 'column');
  validateString(obj.columnId, 'columnId');
  validateString(obj.name, 'name');
  validateString(obj.type, 'type');
  return obj as unknown as Column;
}

/**
 * Validate a document response from the API
 */
export function validateDocument(data: unknown): Record<string, unknown> {
  const obj = validateObject(data, 'document');
  validateString(obj.documentId, 'documentId');
  validateString(obj.fileName, 'fileName');
  return obj;
}

/**
 * Debug helper to check type alignment
 */
export function debugTypeAlignment(data: unknown, typeName: string, _context?: string): void {
  if (process.env.NODE_ENV === 'development') {
    console.debug(`[Type Check] ${typeName}: received`, typeof data, data);
  }
}
